import isNil from 'lodash/isNil';
import cloneDeep from 'lodash/cloneDeep';
import React, { useId } from 'react';
import { DragDropProvider, useDraggable, useDroppable } from '@dnd-kit/react';
import { closestCenter } from '@dnd-kit/collision';
import { arrayMove } from '@dnd-kit/helpers';
import styles from './index.module.styl';
type DraggableEntryComponentType = React.ReactElement<{
  ref: React.Ref<HTMLElement>;
  index: number;
  handleRef?: (element: Element | null) => void;
}>;
type DraggableEntryProps = {
  /** the type of entry for the draggable item */
  type: string;
  /** the type of entries a given item can receive, like filters, breakdowns, or other metrics */
  accepts?: string[];
  index: number;
  parentIndex?: number;
  isLast?: boolean;
  /** when provided, allows for a drag and drop gripper element */
  handleRef?: (element: Element | null) => void;
  children: DraggableEntryComponentType;
};
function DraggableEntryDropTarget(props: {
  id: string;
  index: number;
}) {
  const dragId = useId();
  const {
    ref,
    isDropTarget: isDropping
  } = useDroppable({
    id: "".concat(dragId, "-").concat(props.id),
    collisionDetector: closestCenter,
    data: {
      index: props.index
    }
  });
  return <div className={styles.dropTarget} ref={ref} data-drop-zone data-sentry-component="DraggableEntryDropTarget" data-sentry-source-file="index.tsx">
      {isDropping && <div className={styles.indicator} />}
    </div>;
}
export function DraggableEntry(props: DraggableEntryProps) {
  var _props$parentIndex, _props$parentIndex2;
  if (!props.children || !React.isValidElement(props.children)) {
    throw new Error("DraggableEntry must have exactly one child");
  }
  const id = useId();
  const dragId = "".concat(props.type, "-").concat(id, "-").concat((_props$parentIndex = props.parentIndex) !== null && _props$parentIndex !== void 0 ? _props$parentIndex : "", "-").concat(props.index);
  const index = (_props$parentIndex2 = props.parentIndex) !== null && _props$parentIndex2 !== void 0 ? _props$parentIndex2 : props.index;
  const {
    ref,
    handleRef
  } = useDraggable({
    id: "".concat(dragId, "-draggable"),
    feedback: "clone",
    type: props.type,
    data: {
      index
    }
  });
  const isNested = !isNil(props.parentIndex);
  const disableDrag = isNested ? props.index !== 0 : false;
  const disableDropAbove = isNested ? props.index !== 0 : false;
  if (!props.children || !React.isValidElement(props.children)) {
    throw new Error("DraggableEntry must have exactly one child");
  }
  return <>
      {!disableDropAbove && <DraggableEntryDropTarget id={"".concat(dragId, "-above")} index={props.index} />}

      {React.cloneElement(props.children, {
      ref,
      handleRef: !disableDrag ? handleRef : undefined
    })}
    </>;
}
export function DraggableEntriesList(props: {
  entries: Obj[];
  children: Array<React.ReactElement<typeof DraggableEntry>>;
  setEntries: (entries: Obj[], metadata: {
    oldEntries: Obj[];
    from: number;
    to: number;
  }) => void;
  entryGenerator?: (entry: Obj, metadata: {
    isCloning?: boolean;
  }) => Obj | undefined | null;
}) {
  return <DragDropProvider onDragEnd={event => {
    var _event$nativeEvent;
    const {
      source,
      target
    } = event.operation;
    const isCloning = Boolean((_event$nativeEvent = event.nativeEvent as MouseEvent) === null || _event$nativeEvent === void 0 ? void 0 : _event$nativeEvent.altKey);
    if (event.operation.canceled || !props.entries || !source || !target) {
      return;
    }
    const from = source.data.index;
    const targetIndex = target.data.index;

    // adjust the drop target index to account for the drop target position relative to the source
    const modifier = from > targetIndex ? 0 : -1;
    const to = Math.max(targetIndex + modifier, 0);

    // only move items if shifting the list
    if ((from + 1 === targetIndex || from === targetIndex) && !isCloning) {
      return;
    }
    const draggedEntry = props.entries[from];
    const newEntry = cloneDeep(props.entryGenerator ? props.entryGenerator(draggedEntry, {
      isCloning
    }) : draggedEntry);
    if (!newEntry) {
      return;
    }
    let updatedEntries = cloneDeep(props.entries);
    if (isCloning) {
      updatedEntries.splice(from, 0, newEntry);
    } else {
      updatedEntries = arrayMove(props.entries, from, to);
    }
    props.setEntries(updatedEntries, {
      oldEntries: cloneDeep(props.entries),
      from,
      to
    });
  }} data-sentry-element="DragDropProvider" data-sentry-component="DraggableEntriesList" data-sentry-source-file="index.tsx">
      {/* renders all sortable items */}
      {props.children}

      {/* droppable target for bottom of the list  */}
      <DraggableEntryDropTarget id='last' index={props.entries.length} data-sentry-element="DraggableEntryDropTarget" data-sentry-source-file="index.tsx" />
    </DragDropProvider>;
}